import { Api } from "@vzbl/integrations";
import {
  Aggregation,
  ColumnFormula,
  ColumnHaving,
  Comparator,
  Condition,
  Field,
} from "src/search/types";

export const asKey = (obj: Aggregation | Field | undefined) => {
  if (!obj) return "";
  const { target, attribute } = obj;
  const fieldKey = `${target}.${attribute}`;
  return "reducer" in obj ? `${obj.reducer}(${fieldKey})` : fieldKey;
};

export const aggColumnHeader = (obj: Aggregation) => {
  const key = asKey(obj);
  let filterTag = "";
  if ((obj.filters || [])?.length > 0) {
    // just extract the filtered values + assume just one filter
    const f = obj.filters?.at(0) as Condition;
    const vals = Object.values(f.comparison);
    filterTag = `[${vals.join(",")}]`;
  }
  return `${key}${filterTag}`;
};

export const shortTitle = (obj: Aggregation | Field) => {
  const { attribute } = obj;
  return "reducer" in obj ? `${obj.reducer}(${attribute})` : attribute;
};

const intervalRegexp = /(\w+)\.intervals\((.*)\)/;
const aggRegexp = /(\w+)\((.*)\)/;

export const fromKey = (key: string): Field | Aggregation => {
  const intervalMatch = key.match(intervalRegexp);
  if (intervalMatch) {
    const [_, target, attribute] = intervalMatch;
    return { target, attribute: `intervals(${attribute})` };
  }
  const match = key.match(aggRegexp);
  if (match) {
    const [_, reducer, fieldKey, ..._rest] = match;
    const field = fromKey(fieldKey);
    return { ...field, reducer };
  }
  const [target, ...rest] = key.split(".");
  if (rest.length === 0) return { target: "Event", attribute: target };
  const attribute = rest.join(".");
  return { target, attribute };
};

export const eqColumnHaving = (
  a1: ColumnHaving | undefined,
  a2: ColumnHaving | undefined
) => {
  if (a1 === undefined && a2 === undefined) return true;
  if (a1 === undefined || a2 === undefined) return false;
  return (
    a1.column === a2.column &&
    comparatorKey(a1.comparison) === comparatorKey(a2.comparison)
  );
};

export const eqColumnFormula = (
  a1: ColumnFormula | undefined,
  a2: ColumnFormula | undefined
) => {
  if (a1 === undefined && a2 === undefined) return true;
  if (a1 === undefined || a2 === undefined) return false;
  return a1.name === a2.name && eqFormula(a1.formula, a2.formula);
};

const eqFormula = (
  a1: ColumnFormula["formula"],
  a2: ColumnFormula["formula"]
): boolean => {
  return (
    a1.length === a2.length &&
    a1.every(
      (item, index) =>
        (Array.isArray(item) &&
          Array.isArray(a2[index]) &&
          eqFormula(item, a2[index] as any)) ||
        (typeof item === "string" &&
          typeof a2[index] === "string" &&
          item === a2[index])
    )
  );
};

export const eq = (
  a1: undefined | Field | Aggregation,
  a2: undefined | Field | Aggregation
) => {
  if (a1 === undefined && a2 === undefined) return true;
  if (a1 === undefined || a2 === undefined) return false;
  const sameKey = asKey(a1) === asKey(a2);

  const a1FilterKey =
    "filters" in a1 && (a1.filters || []).map(conditionKey).sort();
  const a2FilterKey =
    "filters" in a2 && (a2.filters || []).map(conditionKey).sort();

  const sameFilters = a1FilterKey === a2FilterKey;
  return sameKey && sameFilters;
};

export const comparatorKey = (c: Comparator) => {
  const operator = Object.keys(c).at(0) as keyof Comparator;
  const vals: string[] = c[operator];
  return `${operator}:${vals.sort().join(",")}`;
};

export const conditionKey = (f: Condition) => {
  return `${asKey(f)}:${comparatorKey(f.comparison)}`;
};

// TODO does not handle tuple values?
//
// With the introduction of the tuple type for comparisons, this may not be returning
// the expected values. Follow the potential source of bugs by inspecting the callers.
//
// There is a nearly identical function call `valueFromRecord` which takes
// into account the possible tuple nature. The return value is not compatible with
// this though, which prevents a simple swap-in
export const colVal = (
  record: Record<string, Api.FieldValue>,
  key: string
): string | number => {
  const container = record[key];
  if (
    typeof container == "object" &&
    !Array.isArray(container) &&
    container != null &&
    "value" in container
  ) {
    return container.value;
  }
  return container as string | number;
};
